import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Plus, Loader2, ChevronLeft } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/apiClient';

const Deko = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  const isLoggedIn = !!localStorage.getItem('decoded_token') || !!localStorage.getItem('decoded_user');

  // Extract analysis ID from URL if on analysis page
  const analysisId = location.pathname.match(/\/analysis\/([^/]+)/)?.[1] || null;

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const fetchSessions = async () => {
    if (!isLoggedIn) return;
    try {
      const data = await apiGet('/api/deko/sessions');
      setSessions(data?.sessions || []);
    } catch { /* ignore */ }
  };

  const loadSession = async (sid) => {
    try {
      const data = await apiGet(`/api/deko/sessions/${sid}`);
      setSessionId(sid);
      setMessages(data?.messages || []);
      setShowSessions(false);
    } catch { /* ignore */ }
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
    setShowSessions(false);
  };

  const sendMessage = async () => {
    const text = message.trim();
    if (!text || loading) return;

    setMessage('');
    setMessages(prev => [...prev, { role: 'user', content: text, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const data = await apiPost('/api/deko/chat', {
        message: text,
        session_id: sessionId,
        page_context: location.pathname,
        analysis_id: analysisId,
      });
      if (!sessionId) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply, timestamp: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I had trouble responding. Please try again.', timestamp: new Date().toISOString() }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isLoggedIn) return null;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => { setOpen(true); fetchSessions(); }}
          className="fixed bottom-16 right-24 z-[9999] w-16 h-16 rounded-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-110 ring-4 ring-[#1E40AF]/20"
          data-testid="deko-bubble"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-16 right-24 z-[9999] w-[400px] h-[560px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="deko-window">
          {/* Header */}
          <div className="bg-[#1E40AF] px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              {showSessions && (
                <button onClick={() => setShowSessions(false)} className="text-white/80 hover:text-white">
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-sm font-bold text-white">D</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Deko</p>
                <p className="text-[10px] text-white/60">Decoded AI Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setShowSessions(true); fetchSessions(); }}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Chat history"
                data-testid="deko-history-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </button>
              <button
                onClick={startNewSession}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="New chat"
                data-testid="deko-new-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid="deko-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Session list */}
          {showSessions ? (
            <div className="flex-1 overflow-y-auto p-3">
              <button
                onClick={startNewSession}
                className="w-full text-left px-3 py-2.5 mb-2 rounded-xl bg-[#1E40AF]/5 hover:bg-[#1E40AF]/10 border border-[#1E40AF]/20 text-sm font-medium text-[#1E40AF] transition-colors"
                data-testid="deko-new-session"
              >
                <Plus className="w-4 h-4 inline mr-2" /> New conversation
              </button>
              {sessions.length === 0 && (
                <p className="text-xs text-slate-400 text-center mt-8">No previous conversations</p>
              )}
              {sessions.map(s => (
                <button
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  className={`w-full text-left px-3 py-2.5 mb-1 rounded-xl hover:bg-slate-50 transition-colors ${sessionId === s.session_id ? 'bg-slate-100' : ''}`}
                  data-testid={`deko-session-${s.session_id}`}
                >
                  <p className="text-sm text-slate-700 truncate">{s.title || 'Untitled'}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{new Date(s.created_at).toLocaleDateString()}</p>
                </button>
              ))}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center mt-12">
                    <div className="w-16 h-16 rounded-full bg-[#1E40AF]/10 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-[#1E40AF]">D</span>
                    </div>
                    <p className="text-sm font-semibold text-slate-700 mb-1">Hi! I'm Deko</p>
                    <p className="text-xs text-slate-400 mb-6 max-w-[250px] mx-auto">Your Decoded assistant. Ask me about features, HS codes, or your analysis results.</p>
                    <div className="space-y-2">
                      {['How do I classify a product?', 'What are my top savings?', 'Explain compliance risks'].map(q => (
                        <button
                          key={q}
                          onClick={() => { setMessage(q); }}
                          className="block w-full text-left px-3 py-2 text-xs text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors"
                          data-testid={`deko-suggestion-${q.slice(0,10).replace(/\s/g,'-')}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[#1E40AF] text-white rounded-br-md'
                          : 'bg-slate-100 text-slate-700 rounded-bl-md'
                      }`}
                      data-testid={`deko-msg-${i}`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-slate-100 shrink-0">
                <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-1.5">
                  <input
                    ref={inputRef}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask Deko anything..."
                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 py-1.5"
                    disabled={loading}
                    data-testid="deko-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!message.trim() || loading}
                    className="p-2 rounded-lg bg-[#1E40AF] text-white disabled:opacity-40 hover:bg-[#1E3A8A] transition-colors"
                    data-testid="deko-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Deko;
